import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * 轻量吐司提示。
 *
 * 原来「已保存」这类提示是页面上一行常驻的 `<p class="notice">`：
 * 它不消失，于是越操作越堆，越堆越挡视线，最后没人看。
 * 吐司自动消失，且一次只留最新的几条 —— 反馈应该是"响一下"，
 * 不是"挂在那儿等你处理"。
 *
 * 错误不走这里：���误必须留在页面上，直到人处理掉。所以页面里
 * `error` 与 `notice` 分开存，错误仍然常驻。
 */

export type ToastTone = 'success' | 'info';

export interface ToastItem {
  id: number;
  tone: ToastTone;
  text: string;
}

const LIFETIME = 3200;

export function useToasts() {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);
  const timers = useRef<number[]>([]);

  const dismiss = useCallback((id: number) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const push = useCallback(
    (text: string, tone: ToastTone = 'success') => {
      seq.current += 1;
      const id = seq.current;
      setItems((prev) => [...prev.slice(-2), { id, tone, text }]);
      const timer = window.setTimeout(() => dismiss(id), LIFETIME);
      timers.current.push(timer);
    },
    [dismiss],
  );

  // 组件卸载时清掉定时器，避免对已卸载组件 setState
  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending) window.clearTimeout(timer);
    };
  }, []);

  return { items, push, dismiss };
}

export function Toaster({
  items,
  onDismiss,
}: {
  items: ToastItem[];
  onDismiss: (id: number) => void;
}) {
  if (items.length === 0) return null;
  return (
    <div className="toaster" role="status" aria-live="polite">
      {items.map((item) => (
        <button
          key={item.id}
          className={`toast toast--${item.tone}`}
          onClick={() => onDismiss(item.id)}
          title="点一下关掉"
        >
          {item.text}
        </button>
      ))}
    </div>
  );
}

/**
 * 相对时间。控制台里"最后更新"用绝对时间不够有用 ——
 * 客户真正想知道的是"我刚改的东西在第几页"。
 */
export function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diff = Date.now() - then;
  const minute = 60_000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (diff < minute) return '刚刚';
  if (diff < hour) return `${Math.floor(diff / minute)} 分钟前`;
  if (diff < day) return `${Math.floor(diff / hour)} 小时前`;
  if (diff < 30 * day) return `${Math.floor(diff / day)} 天前`;
  return new Date(iso).toLocaleDateString('zh-CN');
}
